"use client"

import { useCallback, useEffect, useState } from "react"
import { usePathname } from "next/navigation"

import { recordVisitedRoute } from "@/lib/analytics/session-diagnostics"

import { submitBugReport } from "./controller"
import { collectDiagnostics } from "./diagnostics"
import type { BugReportStatus } from "./types"

/**
 * Anota por onde o usuário passou.
 *
 * Mora aqui, e não no `GlobalErrorHandler`, porque a trilha só interessa a quem
 * vai ler o relato — e o widget do relato é o único componente montado na raiz
 * que já precisa do `usePathname`.
 */
export function useRouteTrail(): void {
  const pathname = usePathname()

  useEffect(() => {
    recordVisitedRoute(pathname)
  }, [pathname])
}

export interface BugReportFields {
  description: string
  expected: string
}

export function useBugReport() {
  const [status, setStatus] = useState<BugReportStatus>("idle")
  const [error, setError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setStatus("idle")
    setError(null)
  }, [])

  /** `true` quando o e-mail saiu. O texto digitado só é descartado nesse caso. */
  const submit = useCallback(
    async (fields: BugReportFields): Promise<boolean> => {
      setStatus("sending")
      setError(null)

      try {
        const result = await submitBugReport({
          description: fields.description,
          expected: fields.expected.trim() || undefined,
          diagnostics: collectDiagnostics(),
        })

        if (!result.success) {
          setStatus("error")
          setError(result.error)
          return false
        }

        setStatus("idle")
        return true
      } catch (cause) {
        console.error("[useBugReport] envio falhou", cause)
        setStatus("error")
        setError("Falha de conexão. Verifique a internet e tente de novo.")
        return false
      }
    },
    []
  )

  return { status, error, submit, reset }
}
